import React, { useState } from 'react';
import { Card, Text, Grid, Button, Spacer } from "@nextui-org/react";

// Types based on stateMachineDemo.json
export type StepConfig =
  | string
  | { variable: string; options: Record<string, string>; default: string } // Home/Branching
  | { stateType: 'video'; videos: Record<string, { videoId: string; playbackId: string } | string>; titleKey: string; nextState: string }
  | { stateType: 'survey'; surveyId: string; nextState: string }
  | { stateType: 'videoConference'; Hyperbeam: { urls: string[] }; nextState: string }
  | null; // End state

interface WorkshopStepsProps {
  config: Record<string, StepConfig>;
}

export const WorkshopSteps: React.FC<WorkshopStepsProps> = ({ config }) => {
  const steps = Object.keys(config);
  const [selectedStep, setSelectedStep] = useState<string | null>(steps[0] || null);

  const renderDetail = (stepName: string) => {
    const step = config[stepName];

    if (step === null) {
       return (
        <Card>
            <Card.Body>
                <Text h4>End State</Text>
                <Text>End of workflow.</Text>
            </Card.Body>
        </Card>
       );
    }

    if (typeof step === 'string') {
        return (
            <Card>
                <Card.Body>
                    <Text h4>Simple Transition</Text>
                    <Text>This state immediately transitions to:</Text>
                    <Text b>{step}</Text>
                </Card.Body>
            </Card>
        );
    }

    // Branching (Home)
    if ('variable' in step) {
        return (
             <Card>
                <Card.Body>
                    <Text h4>Branching Logic</Text>
                    <Spacer y={0.5} />
                    <Text>Variable: <Text b>{step.variable}</Text></Text>
                    <Text>Default: <Text b>{step.default}</Text></Text>
                    <Spacer y={1} />
                    <Text h5>Options:</Text>
                    <Grid.Container gap={1}>
                        {Object.entries(step.options).map(([key, val]) => (
                            <Grid xs={12} key={key}>
                                <Text css={{ marginLeft: '$4' }}>• {key} → <Text b>{val}</Text></Text>
                            </Grid>
                        ))}
                    </Grid.Container>
                </Card.Body>
            </Card>
        );
    }

    if ('stateType' in step) {
        switch(step.stateType) {
            case 'video':
                return (
                    <Card>
                        <Card.Body>
                            <Text h4>Video Step</Text>
                            <Spacer y={0.5} />
                            <Text>Title Key: <Text b>{step.titleKey}</Text></Text>
                            <Text>Next State: <Text b>{step.nextState}</Text></Text>
                            <Spacer y={1} />
                            <Text h5>Videos:</Text>
                            <Grid.Container gap={1}>
                            {Object.entries(step.videos).map(([lang, data]) => (
                                <Grid xs={12} key={lang} direction="column">
                                    <Text b>{lang}:</Text>
                                    {typeof data === 'string' ? <Text css={{ marginLeft: '$4' }}>{data}</Text> : (
                                        <div style={{ marginLeft: '1rem' }}>
                                            <Text size="$sm">Video ID: {data.videoId}</Text>
                                            <Text size="$sm">Playback ID: {data.playbackId}</Text>
                                        </div>
                                    )}
                                </Grid>
                            ))}
                            </Grid.Container>
                        </Card.Body>
                    </Card>
                );
            case 'survey':
                 return (
                    <Card>
                        <Card.Body>
                            <Text h4>Survey Step</Text>
                            <Spacer y={0.5} />
                            <Text>Survey ID: <Text b>{step.surveyId}</Text></Text>
                            <Text>Next State: <Text b>{step.nextState}</Text></Text>
                        </Card.Body>
                    </Card>
                );
            case 'videoConference':
                  return (
                    <Card>
                        <Card.Body>
                            <Text h4>Video Conference Step</Text>
                            <Spacer y={0.5} />
                            <Text>Next State: <Text b>{step.nextState}</Text></Text>
                            <Spacer y={1} />
                            <Text h5>Hyperbeam URLs:</Text>
                            <ul>
                                {step.Hyperbeam.urls.map((url, i) => (
                                    <li key={i}><a href={url} target="_blank" rel="noreferrer">{url}</a></li>
                                ))}
                            </ul>
                        </Card.Body>
                    </Card>
                );
            default:
                return <Text>Unknown state type</Text>;
        }
    }

    return <Text>Unknown step format</Text>;
  };

  return (
    <Grid.Container gap={2} css={{ height: 'calc(100vh - 150px)' }}>
        <Grid xs={12} md={4} css={{ height: '100%' }}>
            <Card css={{ height: '100%', overflowY: 'auto' }}>
                <Card.Header><Text h4>Workflow Steps</Text></Card.Header>
                <Card.Divider />
                <Card.Body css={{ padding: '$4' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {steps.map(step => (
                        <Button
                            key={step}
                            light={selectedStep !== step}
                            flat={selectedStep === step}
                            color={selectedStep === step ? "primary" : "default"}
                            onClick={() => setSelectedStep(step)}
                            css={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            {step}
                        </Button>
                    ))}
                    </div>
                </Card.Body>
            </Card>
        </Grid>
        <Grid xs={12} md={8} css={{ height: '100%' }}>
            <div style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
            {selectedStep ? renderDetail(selectedStep) : (
                <Card>
                    <Card.Body>
                        <Text>Select a step to view details.</Text>
                    </Card.Body>
                </Card>
            )}
            </div>
        </Grid>
    </Grid.Container>
  );
};
